import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import "./tailwind.css";

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
} from "@remix-run/react";
import type { LinksFunction, LoaderFunction } from "@remix-run/node";
import React, { useEffect, useState } from "react";
import { getUserId } from "~/src/logic/UserSessionLogic";
import {
  ACCOUNT_SETTINGS_URI_PATH,
  LOGIN_URI_PATH,
  LOGOUT_URI_PATH,
  M_URI_FULL_ROOT_PATH,
  REGISTER_URI_PATH
} from "~/src/types/openTypes";
import CustomErrorBoundary from "~/src/uiblocks/ErrorBoundary";

type LayoutProps = {
  children: React.ReactNode
  userId?: string;
};

type NavProps = {
  userId?: string;
};

export const loader: LoaderFunction = async ({ request }) => {
  const userId = await getUserId(request)
  console.log(process.env.GTM_ENABLED, process.env.GTM_ENABLED == 'true');
  return {
    userId,
    gtmEnabled: process.env.GTM_ENABLED == 'true',
  }
}

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

function Navbar({ userId }: NavProps) {
  const [ isOpen, setIsOpen ] = useState(false);

  const location = useLocation();
  const isInMSection = location.pathname.startsWith(M_URI_FULL_ROOT_PATH);
  const linkToHome = isInMSection ? M_URI_FULL_ROOT_PATH : "/";

  return (
    <header className="bg-white backdrop-blur-md border-b border-blue-200 sticky top-0 z-50">
      {/*<header className="bg-white shadow-md">*/}
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        {/*<div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">*/}
        <div className="flex gap-1 items-center justify-between text-xl font-bold text-blue-600">
          <a href={linkToHome}>
            <img
              src="/favicon.ico"
              alt="WayPick"
              className="w-8 h-8"
            />
          </a>
          <a
            href={linkToHome}
            className="pl-2"
          >
            <h1 className="text-2xl font-bold text-blue-600">
              WayPick
            </h1>
          </a>
        </div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center space-x-4 text-sm font-medium text-gray-600">
          {/*<a*/}
          {/*  href="#tally-open=w2992j&tally-emoji-text=👋&tally-emoji-animation=wave"*/}
          {/*  className="hover:text-blue-600 transition-colors"*/}
          {/*>*/}
          {/*  Leave Feedback*/}
          {/*</a>*/}
          <a
            href={M_URI_FULL_ROOT_PATH}
            className="hover:text-blue-600 transition-colors"
          >
            Create route
          </a>

          {userId && (
            <a
              href={ACCOUNT_SETTINGS_URI_PATH}
              className="hover:text-blue-600 transition-colors"
            >
              Account
            </a>
          )}

          <a
            href={userId ? LOGOUT_URI_PATH : LOGIN_URI_PATH}
            className="hover:text-blue-600 transition-colors"
          >
            {userId ? 'Log out' : 'Log in'}
          </a>
          {!userId && (
            <>
              <a
                href={REGISTER_URI_PATH}
                className="ml-2 inline-block px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                Sign up
              </a>
            </>
          )}
        </nav>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden focus:outline-none"
          aria-label="Toggle menu"
        >
          <svg
            className="w-6 h-6 text-gray-800"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav className="md:hidden px-4 pb-4 space-y-3 text-sm font-medium text-gray-600">
          {/*<a*/}
          {/*  href="#tally-open=w2992j&tally-emoji-text=👋&tally-emoji-animation=wave"*/}
          {/*  className="block hover:text-blue-600 transition-colors"*/}
          {/*>*/}
          {/*  Leave Feedback*/}
          {/*</a>*/}
          <a
            href={M_URI_FULL_ROOT_PATH}
            className="block hover:text-blue-600 transition-colors"
          >
            Create route
          </a>
          {userId && (
            <a
              href={ACCOUNT_SETTINGS_URI_PATH}
              className="block hover:text-blue-600 transition-colors"
            >
              Account
            </a>
          )}
          <a
            href={userId ? LOGOUT_URI_PATH : LOGIN_URI_PATH}
            className="block hover:text-blue-600 transition-colors"
          >
            {userId ? 'Log out' : 'Log in'}
          </a>
          {!userId && (
            <>
              <a
                href={REGISTER_URI_PATH}
                className="block px-4 py-2 rounded-md bg-blue-600 text-white text-center hover:bg-blue-700 transition"
              >
                Sign up
              </a>
            </>
          )}
          {/*<a*/}
          {/*  href="#"*/}
          {/*  className="block hover:text-blue-600 transition-colors"*/}
          {/*>*/}
          {/*  Contact*/}
          {/*</a>*/}
        </nav>
      )}
    </header>
  );
}


function Footer({ userId }: NavProps) {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Grid layout */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-8">

          {/* Logo & Brand */}
          <div className="flex flex-col items-center sm:items-start">
            <div className="flex items-center mb-4">
              <img
                src="/favicon.ico"
                alt="WayPick"
                className="w-8 h-8 mr-3"
              />
              <h1 className="text-xl font-bold">WayPick</h1>
            </div>
            <p className="text-sm text-gray-400 text-center sm:text-left">
              Create unforgettable journeys every day.
            </p>
          </div>

          {/* Account Links */}
          <div className="flex flex-col items-center sm:items-start text-sm text-gray-300">
            <h3 className="font-semibold mb-2 text-white">Account</h3>
            <a
              href={M_URI_FULL_ROOT_PATH}
              className="mb-1 hover:text-white transition"
            >
              Create route
            </a>
            {userId && (
              <a
                href={ACCOUNT_SETTINGS_URI_PATH}
                className="mb-1 hover:text-white transition"
              >
                Account
              </a>
            )}
            <a
              href={userId ? LOGOUT_URI_PATH : LOGIN_URI_PATH}
              className="mb-1 hover:text-white transition"
            >
              {userId ? 'Log out' : 'Log in'}
            </a>
            {!userId && (
              <>
                <a
                  href={REGISTER_URI_PATH}
                  className="mb-1 hover:text-white transition"
                >
                  Sign Up
                </a>
                {/*<a*/}
                {/*  href={FORGOT_PASSWORD_URI_PATH}*/}
                {/*  className="hover:text-white transition"*/}
                {/*>*/}
                {/*  Forgot Password*/}
                {/*</a>*/}
              </>
            )}
          </div>

          {/* Support Links */}
          <div className="flex flex-col items-center sm:items-start text-sm text-gray-300">
            <h3 className="font-semibold mb-2 text-white">Support</h3>
            <a
              href="#tally-open=w2992j&tally-emoji-text=👋&tally-emoji-animation=wave"
              className="mb-1 hover:text-white transition"
            >
              Leave Feedback
            </a>
            {/*<a*/}
            {/*  href="/help"*/}
            {/*  className="mb-1 hover:text-white transition"*/}
            {/*>*/}
            {/*  Help Center*/}
            {/*</a>*/}
            {/*<a*/}
            {/*  href="/contact"*/}
            {/*  className="hover:text-white transition"*/}
            {/*>*/}
            {/*  Contact Us*/}
            {/*</a>*/}
          </div>

          {/* Company Links */}
          <div className="flex flex-col items-center sm:items-start text-sm text-gray-300">
            <h3 className="font-semibold mb-2 text-white">Company</h3>
            {/*<a*/}
            {/*  href="/about"*/}
            {/*  className="mb-1 hover:text-white transition"*/}
            {/*>*/}
            {/*  About Us*/}
            {/*</a>*/}
            {/*<a*/}
            {/*  href="/terms"*/}
            {/*  className="mb-1 hover:text-white transition"*/}
            {/*>*/}
            {/*  Terms of Service*/}
            {/*</a>*/}
            <a
              href="/legal/privacy-policy"
              className="mb-1 hover:text-white transition"
            >
              Privacy Policy
            </a>
            {/*<a*/}
            {/*  href="/careers"*/}
            {/*  className="mb-1 hover:text-white transition"*/}
            {/*>*/}
            {/*  Careers*/}
            {/*</a>*/}
            {/*<a*/}
            {/*  href="/blog"*/}
            {/*  className="hover:text-white transition"*/}
            {/*>*/}
            {/*  Blog*/}
            {/*</a>*/}
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="mt-10 text-center text-gray-500 text-sm">
          &copy; 2025 WayPick. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

function Layout2({ children, userId }: LayoutProps) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar userId={userId} />
      {children}
      <Footer userId={userId} />
    </div>
  );
}

function App() {
  const loaderResult = useLoaderData<typeof loader>();

  return (
    <html
      lang="en"
      translate="no"
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <script
          async
          src="https://tally.so/widgets/embed.js"
        ></script>

        {/* 👉 GTM HEAD */}
        {process.env.NODE_ENV == "production" && loaderResult.gtmEnabled && (
          <>
            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=G-79YXZ7TT2H"
            />
            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=AW-16842699709"
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-79YXZ7TT2H');
                gtag('config', 'AW-16842699709');
          `,
              }}
            />
          </>
        )}

        {/* 👉 Hubspot */}
        <script
          defer={true}
          src="https://js.hsforms.net/forms/embed/43880434.js"
        />

        <Meta />
        <Links />
      </head>
      <body>
        <Layout2 userId={loaderResult.userId}>
          <Outlet />
        </Layout2>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hostname.includes("waypick.app")) {
      const script = document.createElement("script");
      script.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-79YXZ7TT2H');
                gtag('config', 'AW-16842699709');
      `;
      document.head.appendChild(script);
    }
  }, []);

  captureRemixErrorBoundaryError(error);

  return (
    <html
      lang="en"
      translate="no"
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <script
          async
          src="https://tally.so/widgets/embed.js"
        ></script>

        {/* 👉 GTM HEAD */}
        {process.env.NODE_ENV == "production" && (
          <>
            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=G-79YXZ7TT2H"
            />
            <script
              async
              src="https://www.googletagmanager.com/gtag/js?id=AW-16842699709"
            />
          </>
        )}

        {/* 👉 Hubspot */}
        <script
          defer={true}
          src="https://js.hsforms.net/forms/embed/43880434.js"
        />

        <Meta />
        <Links />
      </head>
      <body>
        <Layout2>
          <CustomErrorBoundary />
        </Layout2>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}